import api, { persistAdminSession, clearAdminAuthStorage, getStoredAdmin } from '@/services/api'

/**
 * Admin authentication against the Moroorato backend (/admin/auth/*). Tokens are opaque, persisted by
 * api.js; this module is the single place the panel performs login / fetch-me / logout.
 */

export const login = async ({ email, password, code }) => {
  const { data } = await api.post('/auth/login', {
    email,
    password,
    code, // present only on the second (two-factor) step; ignored by the backend when empty
  })

  const payload = data?.data || {}

  // Enrolled admin: the first call returns mfa_required with NO session — the SPA must prompt for a code.
  if (payload.mfa_required && !payload.session) {
    return { status: 'mfa_required' }
  }

  const admin = payload.admin || null
  const session = payload.session || {}

  persistAdminSession({
    accessToken: session.access_token,
    expiresAt: session.expires_at,
    adminUser: admin,
  })

  return { status: 'ok', admin, mfaEnrollmentRequired: Boolean(payload.mfa_enrollment_required) }
}

/** Is OIDC SSO configured + enabled? Used by the login page to decide whether to show the SSO button. */
export const oidcEnabled = async () => {
  try {
    const { data } = await api.get('/auth/oidc/status')
    return Boolean(data?.data?.enabled)
  } catch (_error) {
    return false
  }
}

/** Begin the SSO flow: returns the provider authorize URL the browser should be sent to. */
export const oidcStart = async () => {
  const { data } = await api.post('/auth/oidc/start')
  return data?.data?.authorize_url || null
}

/** Complete the SSO callback (exchange code+state for a session), persisting it exactly like password login. */
export const oidcCallback = async ({ code, state }) => {
  const { data } = await api.post('/auth/oidc/callback', { code, state })
  const payload = data?.data || {}
  const admin = payload.admin || null
  const session = payload.session || {}

  persistAdminSession({
    accessToken: session.access_token,
    expiresAt: session.expires_at,
    adminUser: admin,
  })

  return { status: 'ok', admin, mfaEnrollmentRequired: Boolean(payload.mfa_enrollment_required) }
}

export const fetchMe = async () => {
  const { data } = await api.get('/auth/me')
  const admin = data?.data?.admin || null
  if (admin) {
    persistAdminSession({ adminUser: admin })
  }
  return admin
}

export const logout = async () => {
  try {
    await api.post('/auth/logout')
  } catch (_error) {
    // Even if the revoke call fails (already expired, offline), clear local state so the user is logged out.
  } finally {
    clearAdminAuthStorage()
  }
}

export const currentAdmin = () => getStoredAdmin()
